import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import type { ReactNode } from "react";
import { baeTypography } from "../theme/baeTheme";
import { elevation, motion, PHONE } from "./tokens";

const useStyles = makeStyles({
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(9.5rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  tile: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gridTemplateRows: "auto auto",
    columnGap: tokens.spacingHorizontalS,
    rowGap: "2px",
    alignItems: "center",
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: elevation.restLight,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  icon: {
    gridRow: "1 / span 2",
    display: "grid",
    placeItems: "center",
    width: "2.25rem",
    height: "2.25rem",
    borderRadius: tokens.borderRadiusMedium,
    fontSize: "18px",
    flexShrink: 0,
  },
  brand: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorCompoundBrandForeground1,
  },
  success: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  neutral: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  label: { color: tokens.colorNeutralForeground3, minWidth: 0 },
  // Space Grotesk with tabular figures: a column of counts that does not line up is a column
  // nobody can compare at a glance, and comparing is the whole reason these are next to each other.
  figure: {
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "1.6rem",
    fontWeight: 700,
    letterSpacing: "-0.03em",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE]: { fontSize: "1.35rem" },
  },

  // A proportion bar, for a row of groups being compared against each other.
  meter: {
    position: "relative",
    height: "4px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: "hidden",
  },
  fill: {
    position: "absolute",
    inset: "0 auto 0 0",
    borderRadius: "2px",
    backgroundColor: tokens.colorCompoundBrandStroke,
    transitionProperty: "width",
    transitionDuration: motion.base,
  },
});

/** The row the tiles sit in. Reflows to a stack when they stop fitting. */
export function StatRow({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.row}>{children}</div>;
}

/**
 * One headline number.
 *
 * The icon earns its place by making the three tiles distinguishable at a glance — a row of
 * identical boxes has to be read left to right every time, and these are the numbers someone
 * checks twenty times a day.
 */
export function StatTile({
  icon,
  label,
  value,
  tone = "brand",
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  tone?: "brand" | "success" | "neutral";
}) {
  const styles = useStyles();
  const toneClass =
    tone === "success" ? styles.success : tone === "neutral" ? styles.neutral : styles.brand;

  return (
    <div className={styles.tile}>
      <span className={`${styles.icon} ${toneClass}`}>{icon}</span>
      <Caption1 className={styles.label}>{label}</Caption1>
      <span className={styles.figure}>{value}</span>
    </div>
  );
}

/**
 * A proportion bar.
 *
 * `max` is the largest value in the set rather than the total, because the question a row of
 * these answers is "where is most of it", not "what share of everything is this".
 */
export function Meter({ value, max }: { value: number; max: number }) {
  const styles = useStyles();
  const percent = max > 0 ? Math.max(2, (value / max) * 100) : 0;

  return (
    <div className={styles.meter}>
      <div className={styles.fill} style={{ width: `${percent}%` }} />
    </div>
  );
}
